import { Link } from 'react-router-dom';
import { mangas, featuredMangaSlugs, latestUpdatedSlugs, popularSlugs, categories } from '@/mocks/mangaData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/base/MangaCard';
import SchemaOrg from '@/components/base/SchemaOrg';

const featuredMangas = featuredMangaSlugs.map((s) => mangas.find((m) => m.slug === s)!).filter(Boolean);
const latestMangas = latestUpdatedSlugs.map((s) => mangas.find((m) => m.slug === s)!).filter(Boolean);
const popularMangas = popularSlugs.map((s) => mangas.find((m) => m.slug === s)!).filter(Boolean);

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '蜜桃少年漫画 - 土豪漫画网',
  url: 'https://www.tuhaomanhua.com/',
  description: '通过土豪漫画网漫画全集下拉式免费阅读蜜桃少年漫画，随时下拉浏览全集中文内容，体验免费且便捷的在线服务。',
  inLanguage: 'zh-CN',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.tuhaomanhua.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '土豪漫画网',
  url: 'https://www.tuhaomanhua.com/',
  description: '专业中文漫画在线阅读平台，提供蜜桃少年漫画等海量漫画全集下拉式免费阅读服务。',
  sameAs: [
    'https://weibo.com/tuhaomanhua',
    'https://space.bilibili.com/tuhaomanhua',
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '蜜桃少年漫画可以在线免费阅读吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，土豪漫画网提供的蜜桃少年漫画全集支持下拉式免费阅读，您无需付费即可畅享全部章节内容。',
      },
    },
    {
      '@type': 'Question',
      name: '土豪漫画网有哪些类型的少年漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '土豪漫画网拥有少年热血、恋爱情感、玄幻魔法、搞笑日和、悬疑科幻、武侠修真、恐怖灵异、日常青春等多种类型的少年漫画，满足不同读者的阅读需求。',
      },
    },
    {
      '@type': 'Question',
      name: '漫画多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '土豪漫画网每日更新最新漫画章节，热门连载漫画通常每周更新1-2话，确保读者能够及时追更。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在手机上阅读漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '土豪漫画网支持移动端自适应浏览，您可以通过手机浏览器直接访问网站，享受下拉式流畅阅读体验。',
      },
    },
  ],
};

export default function Home() {
  return (
    <>
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={orgSchema} />
      <SchemaOrg data={faqSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=beautiful%20anime%20style%20illustration%20of%20cherry%20blossom%20trees%20with%20soft%20peach%20pink%20petals%20falling%20under%20warm%20sunlight%20peaceful%20school%20campus%20scene%20with%20gradient%20sky%20dreamy%20atmosphere%20Japanese%20anime%20background%20art%20style%20pastel%20warm%20tones%20cinematic%20composition&width=1600&height=900&seq=hero-manga-bg&orientation=landscape"
              alt="蜜桃少年漫画 - 土豪漫画网"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-4 drop-shadow-lg leading-tight">
                蜜桃少年漫画
              </h1>
              <p className="text-sm md:text-lg text-white/90 max-w-2xl mb-2 drop-shadow">
                漫画全集下拉式免费阅读
              </p>
              <p className="text-xs md:text-sm text-white/70 max-w-xl mb-8 drop-shadow">
                通过土豪漫画网随时下拉浏览全集中文内容，体验免费且便捷的在线漫画阅读服务
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/category/shaonian-rexue"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Featured Manga Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选，品质保证</p>
              </div>
              <Link
                to="/category/shaonian-rexue"
                className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {featuredMangas.map((manga) => (
                <MangaCard key={manga.slug} manga={manga} />
              ))}
            </div>
          </section>

          {/* Latest Updates Section */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                    最近更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">每日更新，精彩不停</p>
                </div>
                <span className="text-xs text-foreground-500 bg-background-200/70 px-3 py-1 rounded-full whitespace-nowrap">
                  更新于 2026-08-04
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {latestMangas.map((manga) => (
                  <MangaCard key={manga.slug} manga={manga} />
                ))}
              </div>
            </div>
          </section>

          {/* Popular Manga Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  热门排行
                </h2>
                <p className="text-sm text-foreground-500 mt-1">读者最爱，人气之选</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {popularMangas.map((manga, index) => (
                <Link
                  key={manga.slug}
                  to={`/manga/${manga.slug}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-all duration-300"
                >
                  <div className="relative flex-shrink-0">
                    <span className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-accent-500 text-white text-xs font-bold flex items-center justify-center z-10">
                      {index + 1}
                    </span>
                    <div className="w-20 h-28 md:w-24 md:h-32 rounded-lg overflow-hidden">
                      <img
                        src={manga.coverImage}
                        alt={`${manga.title} 漫画封面`}
                        title={`${manga.title} - 土豪漫画网热门排行`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground-900 text-sm group-hover:text-primary-500 transition-colors line-clamp-1">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1">
                      <i className="ri-user-line"></i> {manga.author}
                    </p>
                    <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
                      {manga.description}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i> {manga.rating}
                      </span>
                      <span>{manga.status}</span>
                      <span>{manga.chapters.length}话</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Categories Section */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  漫画分类
                </h2>
                <p className="text-sm text-foreground-500 mt-1">多元类型，总有一款适合你</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group p-4 md:p-5 rounded-lg bg-background-50 hover:bg-primary-50/50 transition-all duration-300 text-center cursor-pointer"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                      <i className="ri-book-open-line text-accent-600 text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-sm group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{cat.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-book-read-line', value: '10,000+', label: '漫画作品' },
                { icon: 'ri-user-heart-line', value: '500万+', label: '活跃读者' },
                { icon: 'ri-file-list-3-line', value: '100万+', label: '漫画章节' },
                { icon: 'ri-thumb-up-line', value: '99.2%', label: '好评率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 md:p-6 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500 mt-1">关于土豪漫画网和蜜桃少年漫画的常见疑问</p>
              </div>
              <div className="space-y-3">
                {faqSchema.mainEntity.map((faq, index) => (
                  <details key={index} className="group bg-background-50 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-background-100 transition-colors list-none">
                      <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.name}</h3>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center group-open:rotate-45 transition-transform">
                        <i className="ri-add-line text-accent-600 text-sm"></i>
                      </span>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.acceptedAnswer.text}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}